/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { toast } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CloseButton, Stack, rem } from '@mantine/core';
import { closeAllModals, closeModal } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useTransition } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ModalName } from '..';
import { BadgeSection } from './badge-section';
import { CommentSection } from './comment-section';
import FavoriteDriverSection from './favorite-driver-section';
import { ReasonSection } from './reason-section';
import { StarRatingSection } from './star-rating-section';
import { TipSection } from './tip-section';
import {
  DEFAULT_FORM_VALUES,
  RATING_THRESHOLDS,
  type RatingFormData,
  ratingFormSchema,
} from './types';
import { apiRoutes } from '@/api/api-routes';
import type { CreateRatingOrderBody } from '@/api/order/create-rating-order';
import { useCreateRatingOrder } from '@/api/order/use-create-rating-order';
import { ROUTES } from '@/config/router';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export function RatingDriverModal() {
  const isMobile = useIsMobile();
  const router = useRouter();
  const tCommon = useTranslations('common');
  const queryClient = useQueryClient();

  const [isPendingTip, startTipTransition] = useTransition();
  const { orderDetails: order } = useBookingStore((state) => state);

  const isICTOrder = order?._id?.includes('ICT');
  const tipAmountRef = useRef<number | null>(null);

  const methods = useForm<RatingFormData>({
    resolver: zodResolver(ratingFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = methods;
  const rating = watch('rating');

  const { mutateAsync: createRatingOrder, isPending: isCreatingRating } =
    useCreateRatingOrder({
      onError: (error) => {
        console.error('Error creating rating order:', error);
        toast.error({
          title: error?.response?.data?.title || 'Đã có lỗi xảy ra',
          description: error?.response?.data?.description,
          withIcon: true,
          withCloseButton: false,
        });
      },
    });

  // Reset form when rating changes, but keep the new rating value
  useEffect(() => {
    const currentRating = rating;
    reset({
      ...DEFAULT_FORM_VALUES,
      rating: currentRating,
    });
  }, [rating, reset]);

  const isPositiveRating = rating >= RATING_THRESHOLDS.POSITIVE;
  const isNegativeRating = rating <= RATING_THRESHOLDS.NEGATIVE;
  const onSubmit = async (data: RatingFormData) => {
    if (!order?._id) return;

    tipAmountRef.current = data.tipInput || data.selectedTipOption;

    const body: CreateRatingOrderBody = {
      rated_by: 'user',
      rating: data.rating,
      comment: isNegativeRating
        ? [...data.selectedReasons, data.comment].filter(Boolean).join(', ')
        : data.comment,
      ...(isPositiveRating && { badge_ids: data.selectedBadges }),
    };

    try {
      await createRatingOrder({
        orderId: order._id,
        body,
      });

      const key = isICTOrder
        ? [apiRoutes.intercity.details(order._id)]
        : [apiRoutes.order.detail(order._id)];

      await queryClient.invalidateQueries({ queryKey: key });

      if (tipAmountRef.current) {
        const checkoutUrl = `${ROUTES.checkout('tip', order._id)}?value=${tipAmountRef.current}`;
        startTipTransition(() => {
          router.push(checkoutUrl);
        });
        closeModal(ModalName.RatingDriver);
        return;
      }

      closeModal(ModalName.RatingDriver);
      toast.success({
        title: 'Cảm ơn bạn đã đánh giá cho tài xế của chúng tôi.',
        withIcon: true,
        withCloseButton: false,
      });
    } catch (error) {
      console.error('Error creating rating order:', error);
      toast.error({
        title: error?.response?.data?.title || 'Đã có lỗi xảy ra',
        description: error?.response?.data?.description,
        withIcon: true,
        withCloseButton: false,
      });
    }
  };

  const onError = (errors: any) => {
    console.error('Form validation errors:', errors);
  };

  const isDisableSubmit = useMemo(() => {
    return !isValid;
  }, [isValid]);

  return (
    <>
      <CloseButton
        pos="absolute"
        iconSize={rem(24)}
        variant="white"
        c="neutral.6"
        top={rem(16)}
        style={{
          ...(isMobile
            ? {
                left: rem(16),
                outline: 'none',
              }
            : {
                right: rem(16),
                outline: 'none',
              }),
          boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.10)',
          borderRadius: '50%',
          background: 'white',
          zIndex: 1,
        }}
        onClick={() => closeAllModals()}
      />

      <FormProvider {...methods}>
        <Box
          h={isMobile ? '100svh' : rem(600)}
          w="100%"
          bg="white"
          style={{
            overflow: 'auto',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE & Edge
          }}
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari
            },
          }}
        >
          <Stack w="100%">
            <Stack gap={0}>
              <Stack justify="center" align="center" p={0} bg="primary.0">
                <StarRatingSection />

                <FavoriteDriverSection />
              </Stack>
              <Box
                h={rem(40)}
                w="100%"
                bg="primary.0"
                style={{
                  borderBottomLeftRadius: '50% 100%',
                  borderBottomRightRadius: '50% 100%',
                }}
              />
            </Stack>

            {isPositiveRating ? <TipSection /> : null}

            {isPositiveRating ? (
              <BadgeSection />
            ) : (
              isNegativeRating && <ReasonSection />
            )}

            <CommentSection />
          </Stack>
        </Box>

        {/* Submit button */}
        <Box
          pos="sticky"
          bottom={0}
          left={0}
          right={0}
          bg="white"
          p={rem(16)}
          style={{ zIndex: 1 }}
        >
          <Button
            fullWidth
            h={rem(48)}
            fw={700}
            onClick={handleSubmit(onSubmit, onError)}
            loading={isCreatingRating || isPendingTip}
            disabled={isCreatingRating || isDisableSubmit || isPendingTip}
          >
            {tCommon('actions.submit')}
          </Button>
        </Box>
      </FormProvider>
    </>
  );
}
